import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ClipboardCheck, Loader2, LogIn, LogOut } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);

const StatusPill = ({ s }) => {
  const map = { Present: "bg-[#2f7d5b]/12 text-[#2f7d5b]", Absent: "bg-destructive/10 text-destructive", Leave: "bg-accent/15 text-accent", "Half Day": "bg-[#3a6ea5]/12 text-[#3a6ea5]" };
  return <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${map[s] || "bg-muted"}`}>{s}</span>;
};

export default function Attendance() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState(today());
  const [marking, setMarking] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/attendance", { params: dateFilter ? { date_filter: dateFilter } : {} })
      .then((r) => setRows(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [dateFilter]);

  const check = async (type) => {
    setMarking(true);
    const time = new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
    try {
      await api.post("/attendance", {
        employee_id: user.id, employee_name: user.name, date: today(),
        status: "Present", [type === "in" ? "check_in" : "check_out"]: time,
      });
      toast.success(type === "in" ? `Checked in at ${time}` : `Checked out at ${time}`);
      setDateFilter(today());
      load();
    } catch (err) { toast.error("Failed to mark attendance"); }
    finally { setMarking(false); }
  };

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground ashoka-underline inline-block">Attendance</h2>
          <p className="text-muted-foreground mt-3">Daily attendance log</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="check-in-button" onClick={() => check("in")} disabled={marking} className="bg-[#2f7d5b] hover:bg-[#2f7d5b]/90 text-white">
            <LogIn className="w-4 h-4 mr-1.5" /> Check In
          </Button>
          <Button data-testid="check-out-button" onClick={() => check("out")} disabled={marking} variant="outline" className="border-primary/30 text-primary">
            <LogOut className="w-4 h-4 mr-1.5" /> Check Out
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <label className="text-sm font-semibold text-muted-foreground">Date</label>
        <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="w-44" data-testid="attendance-date-filter" />
      </div>

      <div className="bg-card rounded-xl border border-border overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground flex flex-col items-center gap-2">
            <ClipboardCheck className="w-8 h-8 opacity-40" />
            No attendance records for this date.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/50 text-left">
                  <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Employee</th>
                  <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Date</th>
                  <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Check In</th>
                  <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Check Out</th>
                  <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id} data-testid={`attendance-row-${a.id}`} className="border-b border-border last:border-0 hover:bg-secondary/40 transition-colors">
                    <td className="px-4 py-3 font-medium text-foreground">{a.employee_name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{a.date}</td>
                    <td className="px-4 py-3 font-mono text-xs">{a.check_in || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">{a.check_out || "—"}</td>
                    <td className="px-4 py-3"><StatusPill s={a.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
